import React from 'react';
import classnames from 'classnames';
import styles from './CreateDataProviderModal.module.scss';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { FormControl } from 'react-bootstrap';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import AvatarInput from 'v2/form/AvatarInput/AvatarInput';
import defaultCompanyAvatar from 'v2/images/defaultCompanyAvatar.png';
import RichTextEditor from 'v2/form/RichTextEditor/RichTextEditor';
import { useTranslation } from 'react-i18next';
import { TranslationKeys, PostProcessKeys } from 'translations';

export interface DataProviderFormFields {
  name: string;
  domain: string;
  avatar_url: File | string | null;
  classification: string;
  description: string;
  data_provider?: number | null;
}

interface DataProviderFormProps {
  register: UseFormRegister<DataProviderFormFields>;
  control: Control<DataProviderFormFields>;
  setValue: UseFormSetValue<DataProviderFormFields>;
  readOnly?: boolean;
}

const DataProviderForm = ({
  register,
  control,
  setValue,
  readOnly = false,
}: DataProviderFormProps) => {
  const { t } = useTranslation();
  const avatarValue = useWatch({ control, name: 'avatar_url' });
  const descriptionValue = useWatch({ control, name: 'description' });

  return (
    <div
      className={classnames({
        [styles.readOnlyForm]: readOnly,
      })}
    >
      <AvatarInput
        file={avatarValue || ''}
        defaultImage={defaultCompanyAvatar}
        className={classnames('form-group mt-2', styles.avatarInput)}
        addBtnText="Upload Photo"
        changeBtnText="Upload Photo"
        removeBtnText="Remove Photo"
        onChange={(data) => {
          setValue('avatar_url', data, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
        rounded={true}
        removeBtnVisible={true}
        disabled={readOnly}
      />

      <div className={classnames('mt-5', styles.providerNameAndType)}>
        <div className="w-50">
          <FormLabel
            label={`${t(TranslationKeys.company_one, {
              postProcess: PostProcessKeys.TitleCase,
            })} name`}
            required={false}
          />
          <div className={classnames(styles.inputContainer)}>
            <FormControl
              className={styles.inputText}
              data-cy="datavendor:create-name-field"
              type="text"
              autoComplete="off"
              placeholder={`${t(TranslationKeys.company_one, {
                postProcess: PostProcessKeys.TitleCase,
              })} Name`}
              aria-label="company_name_input"
              readOnly={readOnly}
              {...register('name', { required: true })}
            />
          </div>
        </div>
        <div className="w-50">
          <FormLabel
            label={`${t(TranslationKeys.company_one, {
              postProcess: PostProcessKeys.TitleCase,
            })} type`}
            required={false}
          />
          <div className={classnames(styles.inputContainer)}>
            <FormControl
              className={styles.inputText}
              data-cy="datavendor:create-type-field"
              type="text"
              autoComplete="off"
              placeholder={`${t(TranslationKeys.company_one, {
                postProcess: PostProcessKeys.TitleCase,
              })} Type`}
              aria-label="company_type_input"
              readOnly={readOnly}
              {...register('classification', { required: true })}
            />
          </div>
        </div>
      </div>
      <div className={classnames(styles.providerDescription)}>
        <p className={classnames('mt-4 mb-1', styles.modalSubtitle)}>
          Description
        </p>
        <RichTextEditor
          readOnly={readOnly}
          value={descriptionValue || ''}
          onChange={(data) =>
            setValue('description', data, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
        />
      </div>
    </div>
  );
};

export default DataProviderForm;
